<template>
    <div class="student-collab-detail text-left" :class="{ dark }">
        <div class="student-collab-detail__inner">
            <header class="student-collab-detail__header">
                <div class="student-collab-detail__header-bar">
                    <button
                        type="button"
                        class="student-collab-detail__back-btn"
                        @click="$router.push('/aluno/acompanhamento')"
                    >
                        <v-icon size="18" aria-hidden="true">mdi-arrow-left</v-icon>
                        {{ t.detail.back }}
                    </button>
                    <v-btn
                        variant="tonal"
                        color="primary"
                        size="small"
                        class="text-none student-collab-detail__pdf-btn"
                        :loading="exporting"
                        :disabled="loading || error || !user || exporting"
                        :aria-label="$t('student.collaboratorDetail.exportPdf')"
                        :title="$t('student.collaboratorDetail.exportPdf')"
                        @click="exportPdf"
                    >
                        <v-icon class="me-1" size="18">mdi-file-pdf-box</v-icon>
                        {{ $t('student.collaboratorDetail.export') }}
                    </v-btn>
                </div>
                <div class="student-collab-detail__header-copy">
                    <nav class="student-collab-detail__breadcrumb" aria-label="Breadcrumb">
                        <router-link
                            to="/aluno/acompanhamento"
                            class="student-collab-detail__breadcrumb-link"
                        >
                            {{ $t('student.monitoring.title') }}
                        </router-link>
                        <span class="student-collab-detail__breadcrumb-sep" aria-hidden="true">/</span>
                        <span class="student-collab-detail__breadcrumb-current">
                            {{ user?.name || t.detail.title }}
                        </span>
                    </nav>
                    <h1 class="student-collab-detail__title">
                        {{ user?.name || t.detail.title }}
                    </h1>
                    <p class="student-collab-detail__subtitle">{{ t.detail.subtitle }}</p>
                </div>
            </header>

            <div v-if="loading" class="student-collab-detail__empty">
                <v-progress-circular indeterminate color="primary" size="48" />
            </div>
            <div v-else-if="error" class="student-collab-detail__empty">
                {{ t.common.error }}
            </div>

            <template v-else>
                <section class="student-collab-detail__hero">
                    <div class="student-collab-detail__hero-top">
                        <div
                            class="student-collab-detail__avatar"
                            :class="user.status === 'active' ? 'is-active' : 'is-inactive'"
                            aria-hidden="true"
                        >
                            {{ initials(user.name) }}
                        </div>
                        <div class="student-collab-detail__hero-body">
                            <div class="student-collab-detail__title-row">
                                <h2>{{ user.name }}</h2>
                                <span
                                    class="student-collab-detail__chip"
                                    :class="user.status === 'active' ? 'is-success' : 'is-muted'"
                                >
                                    {{
                                        user.status === 'active'
                                            ? t.collaborators.active
                                            : t.collaborators.inactive
                                    }}
                                </span>
                                <span
                                    v-if="user.isResponsible"
                                    class="student-collab-detail__chip is-accent"
                                >
                                    {{ t.collaborators.responsible }}
                                </span>
                            </div>
                            <p class="student-collab-detail__email">{{ user.email }}</p>
                            <div class="student-collab-detail__meta">
                                <span>{{ user.sectorName || '—' }}</span>
                                <span>{{ user.roleName || '—' }}</span>
                                <span v-if="user.managerName">{{ user.managerName }}</span>
                            </div>
                        </div>
                    </div>
                    <div class="student-collab-detail__overall">
                        <div class="student-collab-detail__overall-meta">
                            <span>{{ t.detail.overallProgress }}</span>
                            <strong>{{ user.progressPercent || 0 }}%</strong>
                        </div>
                        <div
                            class="student-collab-detail__track"
                            role="progressbar"
                            :aria-valuenow="user.progressPercent || 0"
                            aria-valuemin="0"
                            aria-valuemax="100"
                            :aria-label="`${t.detail.overallProgress}: ${user.progressPercent || 0}%`"
                        >
                            <div
                                class="student-collab-detail__fill"
                                :class="progressTone(user.progressPercent)"
                                :style="{ width: `${Math.min(100, user.progressPercent || 0)}%` }"
                            />
                        </div>
                    </div>
                </section>

                <section class="student-collab-detail__stats" :aria-label="$t('student.collaboratorDetail.eduSummary')">
                    <article>
                        <span>{{ t.detail.courses }}</span>
                        <strong>{{ progress.totalAssigned || 0 }}</strong>
                    </article>
                    <article>
                        <span>{{ t.detail.completed }}</span>
                        <strong>{{ progress.completed || 0 }}</strong>
                    </article>
                    <article :class="{ 'is-alert': (progress.overdue || 0) > 0 }">
                        <span>{{ t.detail.overdue }}</span>
                        <strong>{{ progress.overdue || 0 }}</strong>
                    </article>
                    <article>
                        <span>{{ t.detail.points }}</span>
                        <strong>{{ progress.gamificationPoints || 0 }}</strong>
                    </article>
                    <article class="student-collab-detail__stats-level">
                        <span>{{ t.detail.level }}</span>
                        <strong class="is-level">{{ progress.gamificationLevel || '—' }}</strong>
                    </article>
                </section>

                <div class="student-collab-detail__grid">
                    <section class="student-collab-detail__panel">
                        <div class="student-collab-detail__panel-head">
                            <h3>{{ t.detail.orgData }}</h3>
                            <p>{{ t.detail.orgLead }}</p>
                        </div>
                        <dl class="student-collab-detail__fields">
                            <div>
                                <dt>{{ t.collaborators.name }}</dt>
                                <dd>{{ user.name }}</dd>
                            </div>
                            <div>
                                <dt>{{ t.collaborators.email }}</dt>
                                <dd>{{ user.email }}</dd>
                            </div>
                            <div>
                                <dt>{{ t.collaborators.sector }}</dt>
                                <dd>{{ user.sectorName || '—' }}</dd>
                            </div>
                            <div>
                                <dt>{{ t.collaborators.role }}</dt>
                                <dd>{{ user.roleName || '—' }}</dd>
                            </div>
                            <div>
                                <dt>{{ t.collaborators.manager }}</dt>
                                <dd>{{ user.managerName || '—' }}</dd>
                            </div>
                            <div>
                                <dt>{{ t.collaborators.admissionDate }}</dt>
                                <dd>{{ formatDate(user.admissionDate) }}</dd>
                            </div>
                            <div>
                                <dt>{{ t.collaborators.internalCode }}</dt>
                                <dd>{{ user.internalCode || '—' }}</dd>
                            </div>
                            <div>
                                <dt>{{ t.collaborators.status }}</dt>
                                <dd>
                                    <span
                                        class="student-collab-detail__chip"
                                        :class="user.status === 'active' ? 'is-success' : 'is-muted'"
                                    >
                                        {{
                                            user.status === 'active'
                                                ? t.collaborators.active
                                                : t.collaborators.inactive
                                        }}
                                    </span>
                                </dd>
                            </div>
                        </dl>
                    </section>

                    <section class="student-collab-detail__panel student-collab-detail__panel--timeline">
                        <div class="student-collab-detail__panel-head student-collab-detail__panel-head--row">
                            <div class="student-collab-detail__panel-head-copy">
                                <h3>{{ t.detail.timeline }}</h3>
                                <p v-if="showTimelineBody">{{ t.detail.timelineLead }}</p>
                            </div>
                            <button
                                v-if="!isDesktop"
                                type="button"
                                class="student-collab-detail__collapse-toggle"
                                :aria-expanded="timelineOpen"
                                aria-controls="collab-detail-timeline"
                                @click="timelineOpen = !timelineOpen"
                            >
                                {{ timelineOpen ? $t('student.collaboratorDetail.hide') : $t('student.collaboratorDetail.show') }}
                                <v-icon size="18" aria-hidden="true">
                                    {{ timelineOpen ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                                </v-icon>
                            </button>
                        </div>
                        <div
                            v-show="showTimelineBody"
                            id="collab-detail-timeline"
                            class="student-collab-detail__timeline-body"
                        >
                            <div v-if="!timeline.length" class="student-collab-detail__panel-empty">
                                {{ t.detail.timelineEmpty }}
                            </div>
                            <ol v-else class="student-collab-detail__timeline">
                                <li v-for="event in timeline" :key="event.id">
                                    <div class="student-collab-detail__dot" aria-hidden="true" />
                                    <div>
                                        <strong>{{ eventLabel(event) }}</strong>
                                        <p>{{ event.description }}</p>
                                        <time>{{ formatDateTime(event.createdAt) }}</time>
                                    </div>
                                </li>
                            </ol>
                        </div>
                    </section>
                </div>

                <section class="student-collab-detail__panel">
                    <div class="student-collab-detail__panel-head">
                        <h3>{{ t.detail.courseHistory }}</h3>
                        <p>{{ t.detail.courseHistoryLead }}</p>
                    </div>

                    <div
                        v-if="!(progress.enrolments || []).length"
                        class="student-collab-detail__panel-empty"
                    >
                        {{ t.detail.coursesEmpty }}
                    </div>

                    <div v-else class="student-collab-detail__courses">
                        <article
                            v-for="(course, idx) in progress.enrolments"
                            :key="`${course.courseId || course.courseName}-${idx}`"
                            class="student-collab-detail__course"
                        >
                            <div class="student-collab-detail__course-header">
                                <strong class="student-collab-detail__course-name">
                                    {{ course.courseName }}
                                </strong>
                                <div class="student-collab-detail__course-chips">
                                    <span
                                        class="student-collab-detail__chip"
                                        :class="course.mandatory ? 'is-accent' : 'is-muted'"
                                    >
                                        {{
                                            course.mandatory
                                                ? t.coursesPage.mandatory
                                                : t.coursesPage.optional
                                        }}
                                    </span>
                                    <span
                                        class="student-collab-detail__chip"
                                        :class="enrolmentStatusClass(course.status)"
                                    >
                                        {{ enrolmentStatusLabel(course.status) }}
                                    </span>
                                </div>
                            </div>
                            <div class="student-collab-detail__course-meta">
                                {{ t.detail.source }}: {{ sourceLabel(course.source) }}
                            </div>
                            <div class="student-collab-detail__course-progress">
                                <div class="student-collab-detail__course-progress-head">
                                    <span>{{ t.collaborators.progress }}</span>
                                    <strong>{{ course.progress || 0 }}%</strong>
                                </div>
                                <div
                                    class="student-collab-detail__track"
                                    role="progressbar"
                                    :aria-valuenow="course.progress || 0"
                                    aria-valuemin="0"
                                    aria-valuemax="100"
                                    :aria-label="$t('student.collaboratorDetail.progressOf', { name: course.courseName })"
                                >
                                    <div
                                        class="student-collab-detail__fill"
                                        :class="progressTone(course.progress)"
                                        :style="{
                                            width: `${Math.min(100, course.progress || 0)}%`,
                                        }"
                                    />
                                </div>
                            </div>
                        </article>
                    </div>
                </section>
            </template>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import usersApi, { canAccessTeamMonitoring } from '@/utils/corporate/users';
import { getPreviewQueryParams } from '@/utils/corporate/portalViewer';
import { exportCollaboratorPdf } from '@/utils/corporate/exportCollaboratorPdf';
import { corporateI18n } from '@/i18n/mixins';
import { intlLocale } from '@/i18n/locale';

export default {
    name: 'StudentCollaboratorDetail',
    mixins: [corporateI18n],
    data() {
        return {
            loading: true,
            error: false,
            exporting: false,
            user: null,
            progress: {},
            timeline: [],
            timelineOpen: false,
        };
    },
    computed: {
        ...mapState({
            token: 'token',
            dark: 'dark',
            authUser: 'user',
            previewAs: 'previewAs',
        }),
        canAccessMonitoring() {
            return canAccessTeamMonitoring(this.authUser);
        },
        isDesktop() {
            return this.$vuetify?.display?.mdAndUp === true;
        },
        showTimelineBody() {
            return this.isDesktop || this.timelineOpen;
        },
    },
    watch: {
        canAccessMonitoring: {
            immediate: true,
            handler(value) {
                if (!value) {
                    this.$router.replace('/aluno/dashboard');
                }
            },
        },
        isDesktop: {
            immediate: true,
            handler(desktop) {
                if (desktop) {
                    this.timelineOpen = true;
                } else {
                    this.timelineOpen = false;
                }
            },
        },
        '$route.params.id': {
            handler() {
                if (this.canAccessMonitoring) {
                    this.loadDetail();
                }
            },
        },
        previewAs: {
            deep: true,
            handler() {
                if (this.canAccessMonitoring) {
                    this.loadDetail();
                }
            },
        },
    },
    created() {
        if (!this.canAccessMonitoring) {
            this.$router.replace('/aluno/dashboard');
            return;
        }
        this.loadDetail();
    },
    methods: {
        async exportPdf() {
            if (!this.user || this.exporting) return;
            this.exporting = true;
            try {
                await exportCollaboratorPdf({
                    user: this.user,
                    progress: this.progress,
                    timeline: this.timeline,
                    labels: this.t,
                });
            } catch (e) {
                console.error(e);
                alert(this.$t('student.collaboratorDetail.exportError'));
            } finally {
                this.exporting = false;
            }
        },
        async loadDetail() {
            const id = this.$route.params.id;
            this.loading = true;
            this.error = false;
            try {
                const user = await usersApi.getTeamMember(this.token, id, getPreviewQueryParams());
                this.user = user;
                this.progress = user.progress || {
                    completed: 0,
                    inProgress: 0,
                    notStarted: 0,
                    overdue: 0,
                    completionRate: 0,
                    enrolments: [],
                };
                this.timeline = user.timeline || [];
            } catch (e) {
                console.error(e);
                this.error = true;
            } finally {
                this.loading = false;
            }
        },
        initials(name) {
            if (!name) return '?';
            const parts = String(name).trim().split(/\s+/).filter(Boolean);
            if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
            return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
        },
        progressTone(value) {
            const n = Number(value) || 0;
            if (n >= 80) return 'is-high';
            if (n >= 40) return 'is-medium';
            return 'is-low';
        },
        formatDate(value) {
            if (!value) return '—';
            const date = new Date(value);
            if (Number.isNaN(date.getTime())) return value;
            return date.toLocaleDateString(intlLocale(this.$i18n.locale));
        },
        formatDateTime(value) {
            if (!value) return '—';
            const date = new Date(value);
            if (Number.isNaN(date.getTime())) return value;
            return date.toLocaleString(intlLocale(this.$i18n.locale), {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        },
        eventLabel(event) {
            const map = {
                organization_updated: this.t.detail.eventOrgUpdated,
                course_completed: this.t.detail.eventCourseCompleted,
                user_enrolled: this.t.detail.eventEnrolled,
            };
            return map[event.type] || event.type;
        },
        sourceLabel(source) {
            const map = {
                role: this.t.collaborators.role,
                sector: this.t.collaborators.sector,
                group: this.$t('student.collaboratorDetail.group'),
                manual: this.t.detail.sourceManual,
                user: this.t.detail.sourceManual,
            };
            return map[source] || source || '—';
        },
        enrolmentStatusLabel(status) {
            const map = {
                completed: this.t.detail.statusCompleted,
                in_progress: this.t.detail.statusInProgress,
                overdue: this.t.detail.statusOverdue,
                not_started: this.t.detail.statusNotStarted,
            };
            return map[status] || status || '—';
        },
        enrolmentStatusClass(status) {
            if (status === 'completed') return 'is-success';
            if (status === 'overdue') return 'is-danger';
            if (status === 'in_progress') return 'is-accent';
            return 'is-muted';
        },
    },
};
</script>

<style scoped>
.student-collab-detail {
    --scd-bg: #F5F7FA;
    --scd-surface: #FFFFFF;
    --scd-text: #1F2937;
    --scd-text-secondary: #667085;
    --scd-border: #E4E7EC;
    --scd-alert: #D92D20;
    --scd-progress: #1ac4cd;
    --scd-radius: 12px;

    min-height: 60vh;
    padding: 1.5rem 1rem 3rem;
    background: var(--scd-bg);
    color: var(--scd-text);
    text-align: left;
}

.student-collab-detail.dark {
    --scd-bg: #12141a;
    --scd-surface: #1c1f28;
    --scd-text: #f2f4f8;
    --scd-text-secondary: #9aa3b2;
    --scd-border: #2a3040;
    --scd-progress: #5ad4db;
    background: var(--scd-bg);
}

.student-collab-detail__inner {
    max-width: 1100px;
    margin: 0 auto;
    text-align: left;
}

.student-collab-detail__header {
    display: flex;
    flex-direction: column;
    gap: 0.65rem;
    margin-bottom: 1.25rem;
    text-align: left;
}

.student-collab-detail__header-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    min-height: 40px;
}

.student-collab-detail__back-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    min-height: 40px;
    padding: 0.25rem 0.15rem;
    margin: 0;
    border: 0;
    background: transparent;
    color: var(--scd-text-secondary);
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    border-radius: 6px;
}

.student-collab-detail__back-btn:hover,
.student-collab-detail__back-btn:focus-visible {
    color: var(--scd-text);
}

.student-collab-detail__back-btn:focus-visible {
    outline: 2px solid var(--scd-progress);
    outline-offset: 2px;
}

.student-collab-detail__pdf-btn {
    min-height: 36px;
    flex-shrink: 0;
}

.student-collab-detail__header-copy {
    min-width: 0;
    text-align: left;
}

.student-collab-detail__breadcrumb {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.35rem;
    margin-bottom: 0.5rem;
    font-size: 0.8125rem;
    color: var(--scd-text-secondary);
}

.student-collab-detail__breadcrumb-link {
    color: var(--scd-text-secondary);
    text-decoration: none;
}

.student-collab-detail__breadcrumb-link:hover,
.student-collab-detail__breadcrumb-link:focus-visible {
    color: var(--scd-text);
    text-decoration: underline;
}

.student-collab-detail__breadcrumb-sep {
    opacity: 0.7;
}

.student-collab-detail__breadcrumb-current {
    color: var(--scd-text);
    font-weight: 500;
}

.student-collab-detail__title {
    margin: 0 0 0.35rem;
    font-size: 1.5rem;
    font-weight: 700;
    line-height: 1.25;
    color: var(--scd-text);
    text-align: left;
}

.student-collab-detail__subtitle {
    margin: 0;
    max-width: 42rem;
    color: var(--scd-text-secondary);
    font-size: 0.95rem;
    line-height: 1.5;
    text-align: left;
}

.student-collab-detail__empty {
    min-height: 240px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--scd-radius);
    background: var(--scd-surface);
    border: 1px solid var(--scd-border);
    color: var(--scd-text-secondary);
}

.student-collab-detail__hero {
    display: grid;
    grid-template-columns: 1fr minmax(160px, 220px);
    gap: 1.15rem;
    align-items: center;
    padding: 1.2rem 1.25rem;
    margin-bottom: 1rem;
    border-radius: var(--scd-radius);
    background: var(--scd-surface);
    border: 1px solid var(--scd-border);
    text-align: left;
}

.student-collab-detail__hero-top {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 1rem;
    align-items: center;
    min-width: 0;
}

.student-collab-detail__hero-body {
    min-width: 0;
    text-align: left;
}

.student-collab-detail__overall {
    text-align: left;
}

.student-collab-detail__overall-meta {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    margin-bottom: 0.45rem;
}

.student-collab-detail__avatar {
    width: 64px;
    height: 64px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 1.05rem;
    flex-shrink: 0;
}

.student-collab-detail__avatar.is-active {
    background: #e8f8f9;
    color: #0f8f96;
}

.student-collab-detail__avatar.is-inactive {
    background: #eef0f3;
    color: #5c6370;
}

.student-collab-detail.dark .student-collab-detail__avatar.is-active {
    background: #24343a;
    color: #5ad4db;
}

.student-collab-detail.dark .student-collab-detail__avatar.is-inactive {
    background: #2a3040;
    color: #9aa3b2;
}

.student-collab-detail__title-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.25rem;
}

.student-collab-detail__title-row h2 {
    margin: 0;
    font-size: 1.2rem;
    font-weight: 700;
    color: var(--scd-text);
    min-width: 0;
    word-break: break-word;
}

.student-collab-detail__email {
    margin: 0 0 0.5rem;
    color: var(--scd-text-secondary);
    font-size: 0.875rem;
}

.student-collab-detail__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.65rem 1rem;
    font-size: 0.82rem;
    color: var(--scd-text-secondary);
}

.student-collab-detail__overall-meta > span,
.student-collab-detail__overall > span {
    display: block;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--scd-text-secondary);
}

.student-collab-detail__overall-meta strong,
.student-collab-detail__overall strong {
    display: block;
    font-size: 1.45rem;
    color: #0f8f96;
}

.student-collab-detail.dark .student-collab-detail__overall-meta strong {
    color: #5ad4db;
}

.student-collab-detail__stats {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 0.85rem;
    margin-bottom: 1rem;
}

.student-collab-detail__stats article {
    background: var(--scd-surface);
    border: 1px solid var(--scd-border);
    border-radius: 10px;
    padding: 1rem 1.1rem;
    text-align: left;
}

.student-collab-detail__stats article.is-alert {
    border-color: #fda29b;
    background: #fffbfa;
}

.student-collab-detail.dark .student-collab-detail__stats article.is-alert {
    background: #2a1c1c;
    border-color: #7a3b3b;
}

.student-collab-detail__stats article.is-alert strong {
    color: var(--scd-alert);
}

.student-collab-detail__stats span {
    display: block;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--scd-text-secondary);
    margin-bottom: 0.2rem;
}

.student-collab-detail__stats strong {
    font-size: 1.3rem;
    color: var(--scd-text);
}

.student-collab-detail__stats strong.is-level {
    font-size: 1.05rem;
}

.student-collab-detail__grid {
    display: grid;
    grid-template-columns: 1.1fr 0.9fr;
    gap: 1rem;
    margin-bottom: 1rem;
}

.student-collab-detail__panel {
    background: var(--scd-surface);
    border: 1px solid var(--scd-border);
    border-radius: var(--scd-radius);
    padding: 1.15rem 1.25rem 1.35rem;
    margin-bottom: 1rem;
    text-align: left;
}

.student-collab-detail__grid .student-collab-detail__panel {
    margin-bottom: 0;
}

.student-collab-detail__panel-head {
    margin-bottom: 1rem;
    text-align: left;
}

.student-collab-detail__panel-head--row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.75rem;
}

.student-collab-detail__panel-head-copy {
    min-width: 0;
}

.student-collab-detail__panel-head h3 {
    margin: 0 0 0.25rem;
    font-size: 1.05rem;
    color: var(--scd-text);
}

.student-collab-detail__panel-head p {
    margin: 0;
    font-size: 0.82rem;
    color: var(--scd-text-secondary);
}

.student-collab-detail__collapse-toggle {
    display: inline-flex;
    align-items: center;
    gap: 0.2rem;
    min-height: 44px;
    padding: 0.35rem 0.6rem;
    border: 1px solid var(--scd-border);
    border-radius: 8px;
    background: var(--scd-surface);
    color: var(--scd-text);
    font-size: 0.8125rem;
    font-weight: 600;
    cursor: pointer;
    flex-shrink: 0;
}

.student-collab-detail__collapse-toggle:focus-visible {
    outline: 2px solid var(--scd-progress);
    outline-offset: 2px;
}

.student-collab-detail__panel-empty {
    min-height: 100px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--scd-text-secondary);
    font-size: 0.875rem;
    text-align: center;
}

.student-collab-detail__fields {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.85rem 1rem;
    margin: 0;
}

.student-collab-detail__fields dt {
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--scd-text-secondary);
    margin-bottom: 0.15rem;
}

.student-collab-detail__fields dd {
    margin: 0;
    font-size: 0.9rem;
    color: var(--scd-text);
    font-weight: 600;
    word-break: break-word;
}

.student-collab-detail__timeline {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.9rem;
}

.student-collab-detail__timeline li {
    display: grid;
    grid-template-columns: 14px 1fr;
    gap: 0.75rem;
    position: relative;
}

.student-collab-detail__timeline li:not(:last-child)::before {
    content: '';
    position: absolute;
    left: 5px;
    top: 14px;
    bottom: -0.9rem;
    width: 2px;
    background: var(--scd-border);
}

.student-collab-detail__dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    margin-top: 0.3rem;
    background: var(--scd-progress);
    box-shadow: 0 0 0 4px rgba(26, 196, 205, 0.15);
}

.student-collab-detail__timeline strong {
    display: block;
    font-size: 0.88rem;
    color: var(--scd-text);
    margin-bottom: 0.15rem;
}

.student-collab-detail__timeline p {
    margin: 0 0 0.2rem;
    font-size: 0.82rem;
    color: var(--scd-text-secondary);
}

.student-collab-detail__timeline time {
    font-size: 0.72rem;
    color: var(--scd-text-secondary);
}

.student-collab-detail__courses {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.student-collab-detail__course {
    display: flex;
    flex-direction: column;
    gap: 0.55rem;
    padding: 16px;
    border: 1px solid var(--scd-border);
    border-radius: 10px;
    background: var(--scd-bg);
}

.student-collab-detail.dark .student-collab-detail__course {
    background: #151821;
}

.student-collab-detail__course-header {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.5rem 0.75rem;
}

.student-collab-detail__course-name {
    flex: 1 1 12rem;
    min-width: 0;
    color: var(--scd-text);
    font-size: 0.9375rem;
    font-weight: 700;
    line-height: 1.35;
    word-break: break-word;
}

.student-collab-detail__course-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
    flex-shrink: 0;
}

.student-collab-detail__course-meta {
    font-size: 0.78rem;
    color: var(--scd-text-secondary);
}

.student-collab-detail__course-progress {
    width: 100%;
    margin-top: 0.15rem;
}

.student-collab-detail__course-progress-head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: 0.4rem;
    font-size: 0.75rem;
    color: var(--scd-text-secondary);
}

.student-collab-detail__course-progress-head strong {
    color: #0f8f96;
    font-size: 0.875rem;
    font-weight: 700;
}

.student-collab-detail.dark .student-collab-detail__course-progress-head strong {
    color: #5ad4db;
}

.student-collab-detail__track {
    height: 6px;
    border-radius: 999px;
    background: var(--scd-border);
    overflow: hidden;
}

.student-collab-detail__fill {
    height: 100%;
    border-radius: inherit;
}

.student-collab-detail__fill.is-high {
    background: var(--scd-progress);
}

.student-collab-detail__fill.is-medium {
    background: #DC6803;
}

.student-collab-detail__fill.is-low {
    background: var(--scd-alert);
}

.student-collab-detail__chip {
    display: inline-flex;
    align-items: center;
    font-size: 0.6875rem;
    font-weight: 700;
    padding: 0.2rem 0.55rem;
    border-radius: 999px;
    white-space: nowrap;
    flex-shrink: 0;
}

.student-collab-detail__chip.is-success {
    background: #ecfdf3;
    color: #039855;
}

.student-collab-detail__chip.is-muted {
    background: #eef1f6;
    color: #667085;
}

.student-collab-detail__chip.is-accent {
    background: #e8f8f9;
    color: #0f8f96;
}

.student-collab-detail__chip.is-danger {
    background: #fef3f2;
    color: #D92D20;
}

.student-collab-detail.dark .student-collab-detail__chip.is-success {
    background: #1c3228;
}

.student-collab-detail.dark .student-collab-detail__chip.is-muted {
    background: #2a3040;
    color: #9aa3b2;
}

.student-collab-detail.dark .student-collab-detail__chip.is-accent {
    background: #1e3336;
    color: #5ad4db;
}

.student-collab-detail.dark .student-collab-detail__chip.is-danger {
    background: #3a2222;
}

.student-collab-detail :deep(.v-btn:focus-visible),
.student-collab-detail__collapse-toggle:focus-visible {
    outline: 2px solid var(--scd-progress);
    outline-offset: 2px;
}

/* Desktop: course cards side-by-side progress */
@media (min-width: 960px) {
    .student-collab-detail__course {
        display: grid;
        grid-template-columns: 1.4fr 0.9fr;
        grid-template-areas:
            'header progress'
            'meta progress';
        gap: 0.35rem 1rem;
        align-items: center;
    }

    .student-collab-detail__course-header {
        grid-area: header;
    }

    .student-collab-detail__course-meta {
        grid-area: meta;
    }

    .student-collab-detail__course-progress {
        grid-area: progress;
        margin-top: 0;
    }
}

/* Mobile */
@media (max-width: 959px) {
    .student-collab-detail {
        padding: 12px 16px 24px;
        overflow-x: hidden;
    }

    .student-collab-detail__header {
        gap: 0.5rem;
        margin-bottom: 1rem;
    }

    .student-collab-detail__back-btn {
        min-height: 44px;
        font-size: 0.8125rem;
    }

    .student-collab-detail__breadcrumb {
        font-size: 0.75rem;
    }

    .student-collab-detail__title {
        font-size: 1.25rem;
    }

    .student-collab-detail__subtitle {
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        overflow: hidden;
        font-size: 0.8125rem;
        line-height: 1.4;
    }

    .student-collab-detail__hero {
        grid-template-columns: 1fr;
        gap: 1rem;
        padding: 16px;
        border-radius: 12px;
    }

    .student-collab-detail__hero-top {
        grid-template-columns: auto 1fr;
        align-items: flex-start;
        gap: 0.75rem;
    }

    .student-collab-detail__avatar {
        width: 48px;
        height: 48px;
        border-radius: 12px;
        font-size: 0.875rem;
    }

    .student-collab-detail__title-row h2 {
        font-size: 1.05rem;
        line-height: 1.3;
    }

    .student-collab-detail__overall-meta {
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 0.45rem;
    }

    .student-collab-detail__overall-meta > span {
        margin-bottom: 0;
        text-transform: none;
        letter-spacing: 0;
        font-size: 0.8125rem;
        font-weight: 500;
    }

    .student-collab-detail__overall-meta strong {
        font-size: 0.9375rem;
        font-weight: 700;
    }

    .student-collab-detail__stats {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 0.75rem;
    }

    .student-collab-detail__stats article {
        padding: 1rem;
        min-height: 88px;
    }

    .student-collab-detail__stats-level {
        grid-column: 1 / -1;
    }

    .student-collab-detail__stats span {
        font-size: 0.6875rem;
    }

    .student-collab-detail__stats strong {
        font-size: 1.25rem;
    }

    .student-collab-detail__grid {
        grid-template-columns: 1fr;
        gap: 0.85rem;
    }

    .student-collab-detail__panel {
        padding: 16px;
        border-radius: 12px;
        margin-bottom: 0.85rem;
    }

    .student-collab-detail__panel-head {
        margin-bottom: 0.85rem;
    }

    .student-collab-detail__panel-head--row {
        margin-bottom: 0;
    }

    .student-collab-detail__panel-head--row + .student-collab-detail__timeline-body {
        margin-top: 0.85rem;
    }

    .student-collab-detail__fields {
        grid-template-columns: 1fr;
        gap: 0.75rem;
    }

    .student-collab-detail__courses {
        gap: 12px;
    }

    .student-collab-detail__course {
        padding: 16px;
    }
}

@media (max-width: 480px) {
    .student-collab-detail {
        padding: 12px 12px 24px;
    }

    .student-collab-detail__title {
        font-size: 1.2rem;
    }

    .student-collab-detail__stats {
        gap: 0.65rem;
    }

    .student-collab-detail__stats article {
        padding: 0.9rem 0.85rem;
    }
}
</style>
